import { Result, Space, Spin } from 'antd'
import { resolveEditAvailabilityForUser } from '@cims-online/shared'
import type {
  ActOnApprovalOptionInput,
  AdvanceAction,
  AdvanceWorkflowRecordInput,
  RevertAction,
  RevertWorkflowRecordInput,
} from '@cims-online/shared'
import { useCIMSDrawer } from 'components/CIMSDrawer'
import { useAuthContext } from 'context/AuthContext'
import { useProjectContext } from 'context/ProjectContext'
import { simpleSuccessNotification } from 'hooks/notificationHandler'
import { useRouteMatches } from 'hooks/useRouteMatches'
import { useCallback, useState } from 'react'
import {
  useActOnApprovalOptionMutation,
  useAdvanceWorkflowRecordFromRecordsMutation,
  useRevertWorkflowRecordFromRecordsMutation,
  useWorkflowRecordsQuery,
} from 'schema'
import {
  DefaultMutationHookOptions,
  DefaultQueryHookOptions,
} from 'utils/defaults'
import {
  createDisabledOptions,
  createTableActionDisabled,
} from './createDisabled'
import CreateWorkflowRecordDrawer from './components/CreateWorkflowRecordDrawer'
import RecordsTable from './components/RecordsTable'
import type {
  RecordsTableActionDisabled,
  RecordsTableMenuItemAction,
  RecordsTableProps,
} from './components/RecordsTable/types'
import ApprovalActionDrawer from './components/ApprovalActionDrawer'
import WorkflowActionDrawer from './components/WorkflowActionDrawer'
import { useWorkflowActions } from './hooks/useWorkflowActions'
import type { UseWorkflowActionsOptions } from './hooks/useWorkflowActions/types'
import { useRecordsTable } from './hooks/useRecordsTable'
import { useApprovalActions } from './hooks/useApprovalActions'
import type { UseApprovalActionsOptions } from './hooks/useApprovalActions/types'
import { useWorkflowRecordLifecycle } from './hooks/useWorkflowRecordLifecycle'
import { useWorkflowRecordSharePoint } from './hooks/useWorkflowRecordSharePoint'
import ManageWorkflowRecordFilesDrawer from './components/ManageWorkflowRecordFilesDrawer'
import type { RecordsWorkflow, WorkflowRecord } from './types'
import EditWorkflowRecordDrawer from './components/EditWorkflowRecordDrawer'

type RecordsContentProps = {
  deepLink?: RecordsTableProps['deepLink']
  loading: boolean
  onReload: ReturnType<typeof useWorkflowRecordsQuery>['refetch']
  workflow: RecordsWorkflow
}

/** Coordinates record actions, their drawers, and post-mutation refreshes. */
function RecordsContent({
  deepLink,
  loading,
  onReload,
  workflow,
}: RecordsContentProps) {
  const { user } = useAuthContext()
  const { projectId } = useProjectContext()
  const drawer = useCIMSDrawer()
  const [fileRecordId, setFileRecordId] = useState<string | null>(null)
  const [editing, setEditing] = useState<Pick<
    WorkflowRecord,
    'id' | 'recordName'
  > | null>(null)
  const createOptions = workflow.createOptions
  const createDisabled = createTableActionDisabled(
    createDisabledOptions(createOptions)
  )
  const hasAvailableTargetStatus = createOptions.targetStatuses.some(
    ({ enabled, valid }) => enabled && valid
  )
  const projectAdmin = !!user && user.admin(projectId).authorized
  const canCreate =
    !!user &&
    (projectAdmin ||
      workflow.permissions.records.some(
        ({ action, users }) =>
          action === 'workflow_record.create' && users.includes(user.id)
      ))
  const canCreateComments =
    !!user &&
    (projectAdmin ||
      workflow.permissions.records.some(
        ({ action, users }) =>
          action === 'comment.create' && users.includes(user.id)
      ))
  const canRemoveRestore =
    !!user &&
    (projectAdmin ||
      workflow.permissions.records.some(
        ({ action, users }) =>
          action === 'workflow_record.remove_restore' && users.includes(user.id)
      ))
  const onCommentAdded = useCallback(() => onReload(), [onReload])
  const [actOnApprovalOption] = useActOnApprovalOptionMutation(
    DefaultMutationHookOptions
  )
  const onApprovalActionSubmitted = useCallback(
    async (input: ActOnApprovalOptionInput) => {
      const result = await actOnApprovalOption({ variables: { input } })
      const response = result.data?.actOnApprovalOption
      if (!response)
        throw new Error('The approval action did not return an edited record.')

      return { recordName: response.edited.recordName }
    },
    [actOnApprovalOption]
  )
  const onApprovalActionSucceeded = useCallback<
    NonNullable<UseApprovalActionsOptions['onSuccess']>
  >(
    async (_result, { optionLabel, action }) => {
      try {
        await onReload()
      } finally {
        simpleSuccessNotification(
          `${optionLabel} successfully ${action.action}!`
        )
      }
    },
    [onReload]
  )
  const approvalActions = useApprovalActions({
    onSubmit: onApprovalActionSubmitted,
    onSuccess: onApprovalActionSucceeded,
  })
  const approvalSession = approvalActions.session
  const [advanceWorkflowRecord] = useAdvanceWorkflowRecordFromRecordsMutation(
    DefaultMutationHookOptions
  )
  const onAdvanceSubmitted = useCallback(
    async (input: AdvanceWorkflowRecordInput) => {
      const result = await advanceWorkflowRecord({ variables: { input } })
      const response = result.data?.advanceWorkflowRecord
      if (!response)
        throw new Error('Advancement did not return an edited record.')
      return { recordName: response.edited.recordName }
    },
    [advanceWorkflowRecord]
  )
  /** Notifies for a committed action even when refreshing the table fails. */
  const onWorkflowActionSucceeded = useCallback(
    async (message: string) => {
      try {
        await onReload()
      } finally {
        simpleSuccessNotification(message)
      }
    },
    [onReload]
  )
  const onAdvanceSucceeded = useCallback<
    UseWorkflowActionsOptions<AdvanceAction>['onSuccess']
  >(
    ({ recordName }, { action }) =>
      onWorkflowActionSucceeded(
        `${recordName} successfully advanced to ${action.label}`
      ),
    [onWorkflowActionSucceeded]
  )
  const advanceActions = useWorkflowActions<AdvanceAction>({
    onSubmit: onAdvanceSubmitted,
    onSuccess: onAdvanceSucceeded,
  })
  const { onAction: onAdvanceAction } = advanceActions
  const advanceSession = advanceActions.session
  const [revertWorkflowRecord] = useRevertWorkflowRecordFromRecordsMutation(
    DefaultMutationHookOptions
  )
  const onRevertSubmitted = useCallback(
    async (input: RevertWorkflowRecordInput) => {
      const result = await revertWorkflowRecord({ variables: { input } })
      const response = result.data?.revertWorkflowRecord
      if (!response) throw new Error('Revert did not return an edited record.')
      return { recordName: response.edited.recordName }
    },
    [revertWorkflowRecord]
  )
  const onRevertSucceeded = useCallback<
    UseWorkflowActionsOptions<RevertAction>['onSuccess']
  >(
    ({ recordName }, { action }) =>
      onWorkflowActionSucceeded(
        `${recordName} successfully reverted to ${action.label}`
      ),
    [onWorkflowActionSucceeded]
  )
  const revertActions = useWorkflowActions<RevertAction>({
    onSubmit: onRevertSubmitted,
    onSuccess: onRevertSucceeded,
  })
  const { onAction: onRevertAction } = revertActions
  const revertSession = revertActions.session
  const workflowActionPending =
    approvalActions.pending || advanceActions.pending || revertActions.pending
  const actionFormOpen =
    approvalActions.open || advanceActions.open || revertActions.open
  const records = workflow.workflowRecords.records
  const lifecycleActions = useWorkflowRecordLifecycle({
    canRemoveRestore,
    onReload,
  })
  const sharePointActions = useWorkflowRecordSharePoint({
    projectId,
    sharePointEnabled: workflow.workflowPluginSettings.records.length > 0,
    onManageFiles: (record) => setFileRecordId(record.id),
  })
  const actions = useCallback<RecordsTableProps['actions']>(
    (record, utils) => {
      const availability =
        record.editAvailability && user
          ? resolveEditAvailabilityForUser(
              record.editAvailability,
              user.id,
              projectAdmin
            )
          : null
      const existing: ReturnType<RecordsTableProps['actions']> = {
        // RecordsTable omits Edit for removed records.
        edit: {
          permission: true,
          disabled: availability?.enabled
            ? false
            : {
                message:
                  availability?.reason ?? 'No editable fields are available.',
              },
          onClick: () => setEditing(record),
        },
        ...lifecycleActions(record, utils),
        ...sharePointActions(record, utils),
      }
      /** Keeps destination-specific reasons ahead of the shared action-in-progress reason. */
      function createMenuItems<Action extends AdvanceAction | RevertAction>(
        items: readonly Action[],
        onAction: (record: WorkflowRecord, action: Action) => void
      ): RecordsTableMenuItemAction[] {
        return items.map((action) => {
          let disabled: RecordsTableActionDisabled = false
          if (action.disabledReason)
            disabled = { message: action.disabledReason }
          else if (workflowActionPending || actionFormOpen)
            disabled = { message: 'Another workflow action is in progress.' }
          return {
            label: action.label,
            permission: action.authorized,
            disabled,
            onClick: () => onAction(record, action),
          }
        })
      }
      const advance = createMenuItems(
        record.advanceActions?.actions ?? [],
        onAdvanceAction
      )
      const revert = createMenuItems(
        record.revertActions?.actions ?? [],
        onRevertAction
      )
      if (advance.length === 0 && revert.length === 0) return existing
      return { ...existing, workflow: { advance, disabled: false, revert } }
    },
    [
      lifecycleActions,
      projectAdmin,
      sharePointActions,
      user,
      onAdvanceAction,
      onRevertAction,
      workflowActionPending,
      actionFormOpen,
    ]
  )
  const { columns, expandable } = useRecordsTable(
    workflow.recordsTableConfig,
    records,
    {
      canCreate: canCreateComments,
      controller: {
        ...approvalActions,
        pending:
          workflowActionPending || advanceActions.open || revertActions.open,
      },
      onCommentAdded,
    }
  )

  return (
    <>
      <RecordsTable
        actions={actions}
        columns={columns}
        createAction={
          canCreate
            ? {
                disabled: createDisabled,
                label: createOptions.actionLabel,
                onClick: () => {
                  if (hasAvailableTargetStatus) drawer.open()
                },
              }
            : undefined
        }
        deepLink={deepLink}
        expandable={expandable}
        key={workflow.id}
        loading={loading}
        onReload={onReload}
        records={records}
      />
      {editing && (
        <EditWorkflowRecordDrawer
          recordId={editing.id}
          recordName={editing.recordName}
          onClose={() => setEditing(null)}
          onEdited={() => onReload()}
        />
      )}
      {approvalSession && (
        <ApprovalActionDrawer
          key={`approval-${approvalSession.number}`}
          onCancel={approvalActions.close}
          onSubmit={approvalActions.submitSession}
          open={approvalActions.open}
          pending={approvalActions.pending}
          session={approvalSession}
        />
      )}
      {advanceSession && (
        <WorkflowActionDrawer
          operation="advance"
          key={`advance-${advanceSession.number}`}
          onCancel={advanceActions.close}
          onSubmit={advanceActions.submitSession}
          open={advanceActions.open}
          pending={advanceActions.pending}
          session={advanceSession}
        />
      )}
      {revertSession && (
        <WorkflowActionDrawer
          operation="revert"
          key={`revert-${revertSession.number}`}
          onCancel={revertActions.close}
          onSubmit={revertActions.submitSession}
          open={revertActions.open}
          pending={revertActions.pending}
          session={revertSession}
        />
      )}
      {canCreate && (
        <CreateWorkflowRecordDrawer
          createOptions={createOptions}
          onClose={drawer.close}
          onCreated={() => onReload()}
          open={drawer.visible}
          workflowId={workflow.id}
        />
      )}
      {fileRecordId && (
        <ManageWorkflowRecordFilesDrawer
          key={`${projectId}/${workflow.id}/${fileRecordId}`}
          projectId={projectId}
          recordId={fileRecordId}
          onClose={() => {
            setFileRecordId(null)
          }}
          onChanged={() => {
            void onReload()
          }}
        />
      )}
    </>
  )
}

/**
 * Loads one workflow's server-defined records table and renders its Technical
 * Directions records. Keying its content by project and workflow remounts drawer
 * state when navigation selects a different project or workflow.
 */
function Records() {
  const { param } = useRouteMatches()
  const { projectId } = useProjectContext()
  const workflowId = param('workflowId')
  const recordId = param('deepLink')
  const { data, error, loading, refetch } = useWorkflowRecordsQuery({
    ...DefaultQueryHookOptions,
    skip: !workflowId,
    variables: workflowId ? { workflowId } : undefined,
  })
  const workflow = data?.listWorkflows.records[0]

  if (error)
    return (
      <Result
        status="error"
        title="Unable to load Technical Directions records"
        subTitle={error.message}
      />
    )

  if (!workflow && (loading || !workflowId))
    return (
      <Space>
        <Spin />
        <span>Loading Technical Directions records...</span>
      </Space>
    )

  if (!workflow) return <Result status="404" title="Workflow not found" />

  return (
    <RecordsContent
      key={`${projectId}/${workflow.id}`}
      deepLink={recordId}
      loading={loading}
      onReload={refetch}
      workflow={workflow}
    />
  )
}

export default Records
